import JsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import type { GetTransactionsWithReceiptDto } from '@pflegenavi/shared/api';
import {
  TransactionSourceType,
  TransactionType,
  PendingChargeStatus,
} from '@pflegenavi/shared/api';
import {
  useFormatDate,
  useFormatting,
} from '@pflegenavi/frontend/localization';
import { YEAR_MONTH_DAY_SHORT_FORMAT } from '@pflegenavi/shared/constants';
import type { TFunction } from 'react-i18next';
import { useTranslation } from 'react-i18next';
import type { ReceiptAttachment } from './utils/getImagesFromReceiptIds';

import { calculateImagePlacement } from '@pflegenavi/shared/pdf-generation';
import type { PdfResident } from './interfaces/PdfResident';
import { format } from 'date-fns';
import { makeSafeFilename } from '@pflegenavi/shared/utils';

import imageCompression from 'browser-image-compression';
import {
  countPaymentData,
  countUpcomingPaymentDays,
  formatPaymentType,
  formatPaymentStatus,
} from '@pflegenavi/shared/frontend';
import type { PendingCharge as ApiPendingCharge } from '@pflegenavi/frontend/api-nursing-home';

// Define the subset of PendingCharge properties needed for PDF generation
type PdfPendingCharge = Pick<
  ApiPendingCharge,
  | 'id'
  | 'amount'
  | 'charge_date'
  | 'payment_initiation'
  | 'pending_charge_status'
  | 'source_of_mandate'
  | 'additional_data'
>;

// colors used
const DIVIDER_GREY = '#F2F2F2';
const PINK = '#FBE3DE';
const TITLE_GREY = '#919EAB';
const ROWS_TEXT_GREY = '#637381';

// baseline margins applied to all elements rendered to be properly aligned and do not overlap with the header/footer, except the header and footer which only have the left and right baseline margins
export const BASELINE_TOP_MARGIN = 40;
const BASELINE_BOTTOM_MARGIN = 30;
const BASELINE_LEFT_MARGIN = 10;
const BASELINE_RIGHT_MARGIN = 10;

interface HandlePrintProps {
  dateFrom: Date;
  dateTo: Date;
  resident: PdfResident;
  amountOfDepositsCents: number;
  amountOfDepositAdditionalFeesCents: number;
  amountOfDepositIncludedFeesCents: number;
  numberOfDeposits: number;
  numberOfDepositCancellations: number;
  amountOfWithdrawalsCents: number;
  amountOfWithdrawalAdditionalFeesCents: number;
  amountOfWithdrawalIncludedFeesCents: number;
  numberOfWithdrawals: number;
  numberOfWithdrawalCancellations: number;
  receiptImages: ReceiptAttachment[];
  pdfFiles?: ReceiptAttachment[];
  balanceEndCents: number; // Balance at the end of the period
  logo?: string;
  allEntries: GetTransactionsWithReceiptDto[];
  pendingReceipts:
    | Array<{
        id: string;
        amount: number;
        additional_fee_amount: number;
        included_fee_amount: number;
        additional_fees_diverted_to_pflegenavi: boolean;
        receipt_date: Date;
        receipt_state: string;
        created_by_employee_id: string;
        receipt_image_ids?: string[];
        service_provider?: {
          id: string;
          name: string;
          global_service_provider_id: string;
        };
        receipt_type?: {
          id: string;
          name: string;
          requires_service_provider: boolean;
        };
      }>
    | undefined;
  pendingPayments: PdfPendingCharge[] | null;
  pendingReceiptImages: ReceiptAttachment[];
  pendingPdfFiles?: ReceiptAttachment[];
}

export interface Context {
  fCurrencyCents: ReturnType<typeof useFormatting>['fCurrencyCents'];
  fDate: ReturnType<typeof useFormatDate>;
  t: TFunction<'translation', undefined>;
}

interface FormatAccountStatementRowOptions {
  fDate: ReturnType<typeof useFormatDate>;
  fCurrencyCents: ReturnType<typeof useFormatting>['fCurrencyCents'];
  t: TFunction<'translation', undefined>;
}

interface AccountStatementRowModel {
  sequenceNumber: string;
  date: string;
  content: string;
  typeOfEntry: string; // First (bold) line of the entry
  serviceProvider?: string; // Second line of the entry - only applicable for receipts (that are not barauslage)
  note?: string; // Applicable for all types, but may or may not be present
  moneyIn: string;
  moneyOut: string;
  balance: string;
}

function formatAccountStatementRow(
  rowData: GetTransactionsWithReceiptDto,
  previousBalance: number,
  previousSequenceNumber: number,
  imageCount: number,
  pdfCount: number,
  options: FormatAccountStatementRowOptions,
  automaticTransactionNumberingEnabled: boolean
): AccountStatementRowModel {
  const note =
    rowData.sourceType === TransactionSourceType.RecurringItem
      ? rowData.recurringItem?.name
      : rowData.notes;

  const serviceProvider = rowData.serviceProvider
    ? `${options.t(`receiptType.${rowData.receiptType ?? 'Andere'}`)} - ${
        rowData.serviceProvider
      }`
    : undefined;

  const attachmentsCount =
    (imageCount > 0 &&
      pdfCount > 0 &&
      `${options.t('resident-pdf.receipt', {
        count: imageCount,
      })} + ${options.t('resident-pdf.pdf', { count: pdfCount })}`) ||
    (imageCount > 0 &&
      `${options.t('resident-pdf.receipt', { count: imageCount })}`) ||
    (pdfCount > 0 && `${options.t('resident-pdf.pdf', { count: pdfCount })}`) ||
    undefined;

  return {
    sequenceNumber: automaticTransactionNumberingEnabled
      ? rowData.sequenceID ?? '--'
      : String(previousSequenceNumber + 1),
    date: options.fDate(rowData.date, YEAR_MONTH_DAY_SHORT_FORMAT),
    typeOfEntry: (() => {
      const paymentType = formatPaymentType(
        rowData.sourceType,
        rowData.initiatedBy,
        options.t,
        {
          receiptType: rowData.receiptType,
          sourceOfMandate: rowData.sourceOfMandate ?? undefined,
        }
      );

      const paymentStatus = formatPaymentStatus(
        undefined,
        rowData.sourceType,
        rowData.disputed,
        rowData.cancelled,
        options.t,
        {
          receiptDate: rowData.receiptDate,
          cancellationDate: rowData.cancelledSubmissionDate,
          dateFormatter: (date) =>
            options.fDate(date, YEAR_MONTH_DAY_SHORT_FORMAT),
        }
      );

      return paymentStatus ? `${paymentType} (${paymentStatus})` : paymentType;
    })(),
    serviceProvider,
    note,
    moneyIn:
      rowData.type === TransactionType.DEPOSIT
        ? options.fCurrencyCents(
            rowData.amountInCent + rowData.additionalFeeAmountInCent
          )
        : '-- ',
    moneyOut:
      rowData.type === TransactionType.WITHDRAWAL
        ? options.fCurrencyCents(
            Math.abs(rowData.amountInCent + rowData.additionalFeeAmountInCent)
          )
        : '-- ',
    balance: options.fCurrencyCents(
      previousBalance + rowData.amountInCent + rowData.additionalFeeAmountInCent
    ),
    content: [
      serviceProvider,
      note,
      ...(attachmentsCount
        ? [`${options.t('common.attachments')}: ${attachmentsCount}`]
        : []),
    ]
      .filter((v) => Boolean(v))
      .join(`\n`)
      .trim(),
  };
}

export function drawAccountStatement(
  context: Context,
  doc: JsPDF,
  entries: GetTransactionsWithReceiptDto[],
  balanceStart: number,
  receiptImages: ReceiptAttachment[],
  pdfFiles: ReceiptAttachment[],
  dateFrom: Date
): void {
  const { fCurrencyCents, fDate, t } = context;

  // Count unique source files per transaction (not expanded PDF pages).
  // PDF pages share the same image.uri, so deduplicating by URI gives the
  // correct number of original receipt files.
  const uriSets = receiptImages.reduce((acc, attachment) => {
    const uris = acc.get(attachment.transactionData.id) ?? new Set<string>();
    uris.add(attachment.image.uri);
    acc.set(attachment.transactionData.id, uris);
    return acc;
  }, new Map<string, Set<string>>());

  const receiptImageNumberMap = new Map<string, number>();
  uriSets.forEach((uris, id) => receiptImageNumberMap.set(id, uris.size));

  const countAttachmentsReducer = (
    acc: Map<string, number>,
    attachment: ReceiptAttachment
  ) => {
    const numberOfAttachments = acc.get(attachment.transactionData.id) ?? 0;
    acc.set(attachment.transactionData.id, numberOfAttachments + 1);
    return acc;
  };

  const receiptPdfNumberMap = pdfFiles.reduce(
    countAttachmentsReducer,
    new Map<string, number>()
  );

  let previousBalance = balanceStart;
  let previousSequenceNumber = 0;
  const formattedEntries = entries.map((row) => {
    const imageCount = receiptImageNumberMap.get(row.id) ?? 0;
    const pdfCount = receiptPdfNumberMap.get(row.id) ?? 0;
    const result = formatAccountStatementRow(
      row,
      previousBalance,
      previousSequenceNumber,
      imageCount,
      pdfCount,
      {
        t,
        fCurrencyCents,
        fDate,
      },
      true
    );
    previousBalance += row.amountInCent + row.additionalFeeAmountInCent;
    previousSequenceNumber++;
    return result;
  });

  if (formattedEntries.length > 0) {
    formattedEntries.unshift({
      sequenceNumber: '--',
      serviceProvider: undefined,
      moneyIn: '-- ',
      moneyOut: '-- ',
      balance: fCurrencyCents(balanceStart),
      date: fDate(dateFrom, YEAR_MONTH_DAY_SHORT_FORMAT),
      typeOfEntry: `${t('resident-pdf.document.balance-start')}`,
      content: t('resident-pdf.document.balance-start-info'),
      note: undefined,
    });
  }

  // @ts-expect-error again function exists but poor typing
  let accountTableStartY: number = doc.lastAutoTable?.finalY
    ? // @ts-expect-error again function exists but poor typing
      doc.lastAutoTable.finalY + 25
    : 0;

  // If not enough space for title + table header + one row, add a new page
  // and position startY with room for the section title above the table
  const pageHeight = doc.internal.pageSize.getHeight();
  if (accountTableStartY + 50 > pageHeight - BASELINE_BOTTOM_MARGIN) {
    doc.addPage();
    accountTableStartY = BASELINE_TOP_MARGIN + 10;
  }

  autoTable(doc, {
    head: [
      [
        {
          content: 'ID',
          styles: {
            halign: 'left',
            cellWidth: 18,
            cellPadding: {
              right: 4.8,
              top: 1.7,
            },
          },
        },
        t('common.date'),
        t('common.details'),
        {
          content: t('common.deposit'),
          styles: {
            halign: 'right',
            cellWidth: 27, // Enough for e.g. € 49.000,00
            cellPadding: {
              right: 0.5,
              top: 1.7,
            },
          },
        },
        {
          content: t('common.withdrawal'),
          styles: {
            halign: 'right',
            cellWidth: 27, // Enough for e.g. € 49.000,00
            cellPadding: {
              right: 0.5,
              top: 1.7,
            },
          },
        },
        {
          content: t('common.accountBalance'),
          styles: {
            halign: 'right',
            cellWidth: 27, // Enough for € 49.000,00
            cellPadding: {
              right: 2,
              top: 1.7,
            },
          },
        },
      ],
    ],
    body: [
      // fill array with duplicate data every row twice, we need that for styling purpose, the second row will be used as border bottom
      ...formattedEntries.reverse().map((row) => [
        row.sequenceNumber,
        row.date,
        // Add a new line to the content to leave room for the bold title added in didDrawCell
        row.content ? `\n${row.content}` : '',
        row.moneyIn,
        row.moneyOut,
        row.balance,
      ]),
    ],
    theme: 'plain',
    headStyles: {
      fontStyle: 'bold',
      fillColor: 'white',
      textColor: 'black',
      minCellHeight: 2,
    },
    styles: {
      lineWidth: {
        bottom: 0.3,
      },
      lineColor: DIVIDER_GREY,
    },
    rowPageBreak: 'avoid',
    columnStyles: {
      0: {
        cellWidth: 18,
        halign: 'left',
        cellPadding: {
          right: 5,
          top: 1.7,
        },
      },
      1: {
        cellWidth: 22, // Wide enough for 25.06.2023 and 25/06/2023
      },
      2: {},
      3: {
        halign: 'right',
        cellPadding: {
          top: 2,
        },
      },
      4: {
        halign: 'right',
        cellPadding: {
          top: 2,
        },
      },
      5: {
        halign: 'right',
        cellPadding: {
          right: 1.4,
          top: 2,
        },
      },
    },
    // willDrawCell: function (data) {
    //   if (data.row.index % 2 === 0 && data.row.section === 'body') {
    //     // Check if it's the 3rd row
    //     data.row.height = rowZeroHeight; // Set the height of the row to zero
    //   }
    // },
    // didParseCell: function (data) {
    //   if (data.row.index % 2 === 0 && data.row.section === 'body') {
    //     // Check if it's the 3rd row
    //     data.cell.styles.fillColor = DIVIDER_GREY; // Set the background color for the cell
    //   } else {
    //     data.cell.styles.fillColor = 'white';
    //   }
    // },
    didDrawPage: function (data) {
      if (data.pageNumber === 1) {
        // Set Title Style
        doc.setFontSize(12);
        doc.setTextColor(TITLE_GREY);
        // @ts-expect-error it doesn't let me not specify the font family
        doc.setFont(undefined, 'bold');

        const titlePos = data.settings.startY - 5;

        // Add Title
        doc.text(
          t('resident-pdf.document.transactions'),
          data.settings.margin.left + 1.5,
          titlePos
        );
      }
      // If there is no data to show, display a message
      if (entries.length === 0) {
        drawNoDataTableText(
          doc,
          context.t('resident-pdf.document.no-data-table'),
          data.settings.startY
        );
      }
    },
    didDrawCell: (data) => {
      if (
        data.column.index === 2 &&
        data.row.section === 'body' &&
        data.row.index >= 0 // -1 on page break
      ) {
        // We add the title in bold here,
        // behause having different text formatting in one cell is not possible otherwise

        const paddingLeft = 1.75;
        const paddingTop = 4.8;

        doc.setFontSize(10);
        // @ts-expect-error it doesn't let me not specify the font family
        doc.setFont(undefined, 'bold');

        const row = formattedEntries[data.row.index];

        doc.text(
          `${row.typeOfEntry}`,
          data.cell.x + paddingLeft,
          data.cell.y + paddingTop
        );
      }
    },
    startY: accountTableStartY,
    margin: {
      top: BASELINE_TOP_MARGIN,
      bottom: BASELINE_BOTTOM_MARGIN,
      left: BASELINE_LEFT_MARGIN,
      right: BASELINE_RIGHT_MARGIN,
    },
  });
}

const drawNoDataTableText = (doc: JsPDF, text: string, startY: number) => {
  const fontSize = 11;
  doc.setFontSize(fontSize);
  doc.setTextColor(TITLE_GREY);
  const pageSize = doc.internal.pageSize;
  const textWidth =
    (doc.getStringUnitWidth(text) * fontSize) / doc.internal.scaleFactor;
  const textOffset = (pageSize.width - textWidth) / 2;

  // @ts-expect-error it doesn't let me not specify the font family
  doc.setFont(undefined, 'normal');
  doc.text(text, textOffset, startY + 15);
};

interface DrawHeaderOpts {
  nursingHomeName: string;
  residentName: string;
  dateFrom: Date;
  dateTo: Date;
  logo?: string;
}

export const drawHeader = async (
  context: Context,
  doc: JsPDF,
  opts: DrawHeaderOpts
): Promise<void> => {
  const { fDate, t } = context;
  doc.setFillColor(PINK);

  const logo = opts.logo;

  // create a rectangle at the top of the page with a height of 10% of the page
  doc.rect(0, 0, doc.internal.pageSize.width, 35, 'F');

  // header
  autoTable(doc, {
    body: [
      [t('resident-pdf.document.nursing-home'), opts.nursingHomeName],
      [t('resident-pdf.document.resident'), opts.residentName],
      [
        t('resident-pdf.document.time-frame'),
        `${fDate(opts.dateFrom, YEAR_MONTH_DAY_SHORT_FORMAT)} - ${fDate(
          opts.dateTo,
          YEAR_MONTH_DAY_SHORT_FORMAT
        )}`,
      ],
    ],
    theme: 'plain',
    columnStyles: {
      0: {
        fontStyle: 'bold',
        cellWidth: 30,
      },
    },
    styles: {
      cellPadding: 0,
      // minCellHeight: 35,
    },
    startY: 12,
    useCss: true,
  });
  if (!logo) {
    return;
  }

  const img: HTMLImageElement = await new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      resolve(img);
    };
    img.onerror = (err) => {
      reject(err);
    };
    img.crossOrigin = 'Anonymous';
    img.src = logo;
  });

  const { x, y, width, height } = calculateImagePlacement(
    img,
    60,
    35,
    5,
    5,
    10
  );

  doc.addImage(
    img,
    'PNG',
    doc.internal.pageSize.width - 60 + x,
    y,
    width,
    height,
    undefined,
    'FAST'
  );
};

export const drawHeaders = async (
  context: Context,
  doc: JsPDF,
  opts: DrawHeaderOpts
): Promise<void> => {
  const pageCount = doc.internal.pages.length - 1;

  for (let i = 1; i <= pageCount; i++) {
    // Go to page i
    doc.setPage(i);

    await drawHeader(context, doc, opts);
  }
};

export const drawFooter = (
  context: Context,
  doc: JsPDF,
  pageNumber: number,
  pageCount: number
): void => {
  const { t } = context;
  const pageHeight = doc.internal.pageSize.height;

  autoTable(doc, {
    body: [
      [
        {
          content: 'help@pflegenavi.at',
          styles: {
            halign: 'left',
          },
        },
        {
          content: `${t('resident-pdf.document.page')}: ${String(
            pageNumber
          )} ${t('resident-pdf.document.of')} ${String(pageCount)}`,
          styles: {
            halign: 'right',
          },
        },
      ],
    ],
    theme: 'plain',
    startY: pageHeight - 15,
    margin: {
      bottom: 0,
      top: 0,
      left: BASELINE_LEFT_MARGIN,
      right: BASELINE_RIGHT_MARGIN,
    },
  });
};

export const drawFooters = (context: Context, doc: JsPDF): void => {
  const pageCount = doc.internal.pages.length - 1;

  for (let i = 1; i <= pageCount; i++) {
    // Go to page i
    doc.setPage(i);
    //Print Page 1 of 4 for example
    drawFooter(context, doc, i, pageCount);
  }
};

export const drawDate = (context: Context, doc: JsPDF): void => {
  const { fDate, t } = context;

  autoTable(doc, {
    body: [[{ content: fDate(new Date(), YEAR_MONTH_DAY_SHORT_FORMAT) }]],
    theme: 'plain',
    didDrawPage: function (data) {
      // Set Title Style
      doc.setFontSize(12);
      doc.setTextColor(TITLE_GREY);
      // @ts-expect-error it doesn't let me not specify the font family
      doc.setFont(undefined, 'bold');

      // Get Page Height
      const titlePos = data.settings.startY - 2;

      // Add Title
      doc.text(t('common.date'), data.settings.margin.left + 1.5, titlePos);
    },
    startY: BASELINE_TOP_MARGIN + 12,
    margin: {
      bottom: BASELINE_BOTTOM_MARGIN + 13,
      left: BASELINE_LEFT_MARGIN + 155,
      right: BASELINE_RIGHT_MARGIN,
    },
  });
};

interface GenerateHeaderOpts {
  dateFrom: Date;
  dateTo: Date;
  residentBalance: number;
  depositAmount: number;
  depositAdditionalFeeAmount: number;
  depositIncludedFeeAmount: number;
  depositCount: number;
  depositCancellationCount: number;
  withdrawalAmount: number;
  withdrawalAdditionalFeeAmount: number;
  withdrawalIncludedFeeAmount: number;
  withdrawalCount: number;
  withdrawalCancellationCount: number;
  pendingReceiptsTotal: number | undefined;
  pendingReceiptsAdditionalFeeTotal: number | undefined;
  pendingReceiptsIncludedFeeTotal: number | undefined;
  pendingPaymentsTotal: number | undefined;
  pendingReceiptsCount?: number;
  pendingPaymentsCount?: number;
}

export const drawOverview = (
  context: Context,
  doc: JsPDF,
  opts: GenerateHeaderOpts
): void => {
  const { t, fDate, fCurrencyCents } = context;
  const {
    withdrawalAmount,
    withdrawalAdditionalFeeAmount,
    depositAmount,
    depositAdditionalFeeAmount,
    dateFrom,
    dateTo,
    residentBalance,
    withdrawalCount,
    withdrawalCancellationCount,
    depositCount,
    depositCancellationCount,
    pendingReceiptsTotal,
    pendingReceiptsAdditionalFeeTotal,
    pendingPaymentsTotal,
    pendingReceiptsCount,
    pendingPaymentsCount,
  } = opts;
  const residentBalanceAfterPending =
    residentBalance -
    (pendingReceiptsTotal || 0) -
    (pendingReceiptsAdditionalFeeTotal || 0) +
    (pendingPaymentsTotal || 0);
  autoTable(doc, {
    body: [
      [
        {
          content: `${t('resident-pdf.document.account-balance')} ${fDate(
            dateFrom,
            YEAR_MONTH_DAY_SHORT_FORMAT
          )}`,
          styles: { cellWidth: 50 },
        },
        {
          content: fCurrencyCents(
            residentBalance -
              depositAmount -
              depositAdditionalFeeAmount -
              withdrawalAmount -
              withdrawalAdditionalFeeAmount
          ),
          styles: { fontStyle: 'bold' },
        },
        {
          content: '',
          styles: { cellWidth: 50 },
        },
      ],

      [
        {
          content: t('resident-pdf.document.deposits'),
          styles: { cellWidth: 50 },
        },
        {
          content: fCurrencyCents(depositAmount + depositAdditionalFeeAmount),
          styles: { cellWidth: 17 },
        },
        {
          content: `${depositCount}x ${
            depositCount === 1
              ? t('resident-pdf.document.deposit')
              : t('resident-pdf.document.deposits')
          }${
            depositCancellationCount > 0
              ? ` (${depositCancellationCount} ${t(
                  'resident-pdf.document.cancellation',
                  { count: depositCancellationCount }
                )})`
              : ''
          }`,
          styles: {
            cellWidth: 65,
            textColor: ROWS_TEXT_GREY,
          },
        },
      ],

      [
        {
          content: t('resident-pdf.document.withdrawals'),
          styles: { cellWidth: 50 },
        },
        {
          content: fCurrencyCents(
            withdrawalAmount + withdrawalAdditionalFeeAmount
          ),
          styles: { cellWidth: 17 },
        },
        {
          content: `${withdrawalCount}x ${
            withdrawalCount === 1
              ? t('resident-pdf.document.withdrawal')
              : t('resident-pdf.document.withdrawals')
          }${
            withdrawalCancellationCount > 0
              ? ` (${withdrawalCancellationCount} ${t(
                  'resident-pdf.document.cancellation',
                  { count: withdrawalCancellationCount }
                )})`
              : ''
          }`,
          styles: {
            cellWidth: 65,
            textColor: ROWS_TEXT_GREY,
          },
        },
      ],

      [
        {
          content: `${t('resident-pdf.document.account-balance')} ${fDate(
            dateTo,
            YEAR_MONTH_DAY_SHORT_FORMAT
          )}`,
          styles: { cellWidth: 50 },
        },
        {
          content: fCurrencyCents(residentBalance),
          styles: {
            cellWidth: 17,
            fontStyle: 'bold',
          },
        },
      ],
      // @ts-expect-error it doesn't let me not specify the font family
      ...(pendingReceiptsTotal !== undefined
        ? [
            [
              {
                content: t('resident-pdf.document.pending-receipts-total'),
                styles: { cellWidth: 50 },
              },
              {
                content: fCurrencyCents(
                  -pendingReceiptsTotal -
                    (pendingReceiptsAdditionalFeeTotal ?? 0)
                ),
                styles: { cellWidth: 17 },
              },
              {
                content: `${pendingReceiptsCount || 0}x ${
                  (pendingReceiptsCount || 0) === 1
                    ? t('resident-pdf.document.pending-receipt')
                    : t('resident-pdf.document.pending-receipts')
                }`,
                styles: {
                  cellWidth: 65,
                  textColor: ROWS_TEXT_GREY,
                },
              },
            ],
          ]
        : []),
      // @ts-expect-error it doesn't let me not specify the font family
      ...(pendingPaymentsTotal !== undefined
        ? [
            [
              {
                content: t('resident-pdf.document.pending-payments-total'),
                styles: { cellWidth: 50 },
              },
              {
                content: fCurrencyCents(pendingPaymentsTotal),
                styles: { cellWidth: 17 },
              },
              {
                content: `${pendingPaymentsCount || 0}x ${
                  (pendingPaymentsCount || 0) === 1
                    ? t('resident-pdf.document.pending-payment')
                    : t('resident-pdf.document.pending-payments')
                }`,
                styles: {
                  cellWidth: 65,
                  textColor: ROWS_TEXT_GREY,
                },
              },
            ],
          ]
        : []),
      // @ts-expect-error it doesn't let me not specify the font family
      ...(pendingPaymentsTotal !== undefined ||
      pendingReceiptsTotal !== undefined
        ? [
            [
              {
                content: t('resident-pdf.document.balance-pending-total'),
                styles: { textColor: ROWS_TEXT_GREY, fontStyle: 'bold' },
              },
              {
                content: fCurrencyCents(residentBalanceAfterPending),
                styles: { textColor: ROWS_TEXT_GREY, fontStyle: 'bold' },
              },
            ],
          ]
        : []),
    ],
    theme: 'plain',
    tableWidth: 105,
    columnStyles: {
      1: {
        halign: 'right',
        cellWidth: 27,
        cellPadding: {
          right: 5,
          top: 2,
        },
      },
    },
    didParseCell: function (data) {
      // Calculate the total number of rows to determine the final "balance after pending" row
      const totalRows = data.table.body.length;
      const hasPendingData =
        pendingReceiptsTotal !== undefined ||
        pendingPaymentsTotal !== undefined;
      const balanceAfterPendingRowIndex = hasPendingData ? totalRows - 1 : -1;

      // Grey background rows: Initial balance (0), Final balance (3), Balance after pending (last row if exists)
      if (
        data.row.index === 0 ||
        data.row.index === 3 ||
        data.row.index === balanceAfterPendingRowIndex
      ) {
        data.cell.styles.fillColor = DIVIDER_GREY;
      } else {
        // White background for all other rows (deposits, withdrawals, pending totals)
        data.cell.styles.fillColor = 'white';
      }
    },
    didDrawPage: function (data) {
      // Set Title Style
      doc.setFontSize(12);
      doc.setTextColor(TITLE_GREY);
      // @ts-expect-error it doesn't let me not specify the font family
      doc.setFont(undefined, 'bold');

      // Get Page Height
      const titlePos = data.settings.startY - 5;

      // Add Title
      doc.text(
        t('resident-pdf.document.account-overview'),
        data.settings.margin.left + 1.5,
        titlePos
      );
    },
    startY: BASELINE_TOP_MARGIN + 15,
    margin: {
      bottom: BASELINE_BOTTOM_MARGIN + 15,
      left: BASELINE_LEFT_MARGIN,
      right: BASELINE_RIGHT_MARGIN,
    },
  });
};

interface PendingTransactionRowModel {
  date: string;
  details: string;
  deposit: string;
  withdrawal: string;
  pendingBalance: string;
  sortDate: Date;
  typeOfEntry: string; // First line to be bold
  amount: number; // Store the actual amount to fix the bug
  insertedAt?: Date; // For secondary sorting
}

interface ComputedPendingTransaction {
  date: string;
  details: string;
  deposit: string;
  withdrawal: string;
  balance: string;
  amount: number;
  typeOfEntry: string;
  formattedRow: string[];
}

export const computePendingTransactions = (
  context: Pick<Context, 't' | 'fDate' | 'fCurrencyCents'>,
  pendingReceipts:
    | Array<{
        id: string;
        amount: number;
        additional_fee_amount: number;
        included_fee_amount: number;
        additional_fees_diverted_to_pflegenavi: boolean;
        receipt_date: Date;
        receipt_state: string;
        created_by_employee_id: string;
        inserted_at?: Date;
        receipt_image_ids?: string[];
        service_provider?: {
          id: string;
          name: string;
          global_service_provider_id: string;
        };
        receipt_type?: {
          id: string;
          name: string;
          requires_service_provider: boolean;
        };
      }>
    | undefined,
  pendingPayments: Array<PdfPendingCharge & { inserted_at?: Date }> | null,
  currentBalance: number,
  resident: PdfResident,
  pendingPdfFiles: ReceiptAttachment[] = []
): ComputedPendingTransaction[] => {
  const { t, fDate, fCurrencyCents } = context;

  // Convert pending receipts to transaction rows
  const receiptRows: PendingTransactionRowModel[] = (pendingReceipts || []).map(
    (receipt) => {
      // Format service provider info similar to main transactions table
      const serviceProvider = receipt.service_provider
        ? `${t(`receiptType.${receipt.receipt_type?.name ?? 'Andere'}`)} - ${
            receipt.service_provider.name
          }`
        : receipt.receipt_type
        ? t(`receiptType.${receipt.receipt_type.name}`)
        : '';

      // Ensure receipt_date is a Date object for proper formatting
      const receiptDate =
        receipt.receipt_date instanceof Date
          ? receipt.receipt_date
          : receipt.receipt_date
          ? new Date(receipt.receipt_date)
          : null;

      const totalCount = receipt.receipt_image_ids?.length ?? 0;
      const pdfCount = pendingPdfFiles.filter(
        (f) => f.transactionData.id === receipt.id
      ).length;
      const imageCount = totalCount - pdfCount;
      const attachmentsCount =
        (imageCount > 0 &&
          pdfCount > 0 &&
          `${t('resident-pdf.receipt', {
            count: imageCount,
          })} + ${t('resident-pdf.pdf', { count: pdfCount })}`) ||
        (imageCount > 0 &&
          `${t('resident-pdf.receipt', { count: imageCount })}`) ||
        (pdfCount > 0 && `${t('resident-pdf.pdf', { count: pdfCount })}`) ||
        undefined;
      const attachmentsText = attachmentsCount
        ? `${t('common.attachments')}: ${attachmentsCount}`
        : undefined;

      const details = [serviceProvider, attachmentsText]
        .filter(Boolean)
        .join('\n');

      return {
        date: receiptDate
          ? fDate(receiptDate, YEAR_MONTH_DAY_SHORT_FORMAT)
          : '',
        details,
        deposit: '-- ',
        withdrawal: fCurrencyCents(
          receipt.amount + receipt.additional_fee_amount
        ),
        pendingBalance: '', // Will be calculated later
        sortDate: receiptDate || new Date(0),
        typeOfEntry: t('pdf-export.pending-receipt-type'),
        amount: receipt.amount + receipt.additional_fee_amount, // Store the actual amount
        insertedAt: receipt.inserted_at,
      };
    }
  );

  // Convert pending payments to transaction rows
  const paymentRows: PendingTransactionRowModel[] = (pendingPayments || []).map(
    (pendingPayment) => {
      const paymentData = countPaymentData(
        pendingPayment,
        resident,
        t,
        countUpcomingPaymentDays(pendingPayment.charge_date)
      );

      // Get payment status text if applicable
      const statusText = formatPaymentStatus(
        pendingPayment.pending_charge_status,
        undefined,
        false,
        false,
        t
      );

      // Combine type and status for display
      const typeWithStatus = statusText
        ? `${t(paymentData.header)} (${statusText})`
        : t(paymentData.header);

      return {
        date: fDate(
          new Date(pendingPayment.charge_date),
          YEAR_MONTH_DAY_SHORT_FORMAT
        ),
        details: '', // No additional details for payments
        deposit: fCurrencyCents(pendingPayment.amount),
        withdrawal: '-- ',
        pendingBalance: '', // Will be calculated later
        sortDate: new Date(pendingPayment.charge_date),
        typeOfEntry: typeWithStatus,
        amount: pendingPayment.amount, // Store the actual amount
        insertedAt: pendingPayment.inserted_at,
      };
    }
  );

  // Combine and sort by date (descending), then by inserted_at (descending)
  const allRows = [...receiptRows, ...paymentRows].sort((a, b) => {
    const dateDiff = b.sortDate.getTime() - a.sortDate.getTime();
    if (dateDiff !== 0) {
      return dateDiff;
    }

    // Secondary sort by inserted_at if dates are the same (descending)
    if (a.insertedAt && b.insertedAt) {
      return (
        new Date(b.insertedAt).getTime() - new Date(a.insertedAt).getTime()
      );
    }
    return 0;
  });

  // Calculate running pending balance in chronological (ascending) order,
  // then reverse back to descending for display so the top row shows the
  // final projected balance.
  const ascending = [...allRows].reverse();
  let runningBalance = currentBalance;
  const computedAscending: ComputedPendingTransaction[] = ascending.map(
    (row) => {
      // Update balance based on transaction type
      if (row.deposit !== '-- ') {
        // This is a payment (positive) - use the stored amount
        runningBalance += row.amount;
      } else {
        // This is a receipt (negative) - use the stored amount
        runningBalance -= row.amount;
      }

      const balanceStr = fCurrencyCents(runningBalance);

      return {
        date: row.date,
        details: row.details,
        deposit: row.deposit,
        withdrawal: row.withdrawal,
        balance: balanceStr,
        amount: row.amount,
        typeOfEntry: row.typeOfEntry,
        formattedRow: [
          '--',
          row.date,
          // Add a new line to the content to leave room for the bold title added in didDrawCell
          row.details ? `\n${row.details}` : '',
          row.deposit,
          row.withdrawal,
          balanceStr,
        ] as string[],
      };
    }
  );

  return computedAscending.reverse();
};

export const drawPendingTransactions = (
  context: Context,
  doc: JsPDF,
  pendingReceipts:
    | Array<{
        id: string;
        amount: number;
        additional_fee_amount: number;
        included_fee_amount: number;
        additional_fees_diverted_to_pflegenavi: boolean;
        receipt_date: Date;
        receipt_state: string;
        created_by_employee_id: string;
        receipt_image_ids?: string[];
        service_provider?: {
          id: string;
          name: string;
          global_service_provider_id: string;
        };
        receipt_type?: {
          id: string;
          name: string;
          requires_service_provider: boolean;
        };
      }>
    | undefined,
  pendingPayments: PdfPendingCharge[] | null,
  startY: number,
  currentBalance: number,
  resident: PdfResident,
  pendingPdfFiles: ReceiptAttachment[] = []
): void => {
  const { t } = context;

  // Use the new computation function
  const computedTransactions = computePendingTransactions(
    context,
    pendingReceipts,
    pendingPayments,
    currentBalance,
    resident,
    pendingPdfFiles
  );

  if (computedTransactions.length === 0) {
    return;
  }

  const formattedRows = computedTransactions.map((tx) => tx.formattedRow);

  // If not enough space for title + table header + one row, add a new page
  // and position startY with room for the section title above the table
  const pendingPageHeight = doc.internal.pageSize.getHeight();
  let pendingTableStartY = startY;
  if (startY + 50 > pendingPageHeight - BASELINE_BOTTOM_MARGIN) {
    doc.addPage();
    pendingTableStartY = BASELINE_TOP_MARGIN + 10;
  }

  autoTable(doc, {
    head: [
      [
        {
          content: 'ID',
          styles: {
            halign: 'left',
            cellWidth: 18,
            cellPadding: {
              right: 4.8,
              top: 1.7,
            },
          },
        },
        t('common.date'),
        t('common.details'),
        {
          content: t('common.deposit'),
          styles: {
            halign: 'right',
            cellWidth: 27, // Enough for e.g. € 49.000,00
            cellPadding: {
              right: 0.5,
              top: 1.7,
            },
          },
        },
        {
          content: t('common.withdrawal'),
          styles: {
            halign: 'right',
            cellWidth: 27, // Enough for e.g. € 49.000,00
            cellPadding: {
              right: 0.5,
              top: 1.7,
            },
          },
        },
        {
          content: t('resident-pdf.document.pending-balance'),
          styles: {
            halign: 'right',
            cellWidth: 27, // Match main table Balance column width
            cellPadding: {
              right: 2,
              top: 1.7,
            },
          },
        },
      ],
    ],
    body: formattedRows,
    theme: 'plain',
    headStyles: {
      fontStyle: 'bold',
      fillColor: 'white',
      textColor: 'black',
      minCellHeight: 2,
    },
    styles: {
      lineWidth: {
        bottom: 0.3,
      },
      lineColor: DIVIDER_GREY,
    },
    rowPageBreak: 'avoid',
    columnStyles: {
      0: {
        cellWidth: 18,
        halign: 'left',
        cellPadding: {
          right: 5,
          top: 1.7,
        },
      },
      1: {
        cellWidth: 22, // Wide enough for 25.06.2023 and 25/06/2023
      },
      2: {},
      3: {
        halign: 'right',
        cellPadding: {
          top: 2,
        },
      },
      4: {
        halign: 'right',
        cellPadding: {
          top: 2,
        },
      },
      5: {
        halign: 'right',
        cellWidth: 27, // Match main table Balance column width
        cellPadding: {
          right: 1.4,
          top: 2,
        },
      },
    },
    didDrawCell: (data) => {
      if (
        data.column.index === 2 &&
        data.row.section === 'body' &&
        data.row.index >= 0 // -1 on page break
      ) {
        // We add the type of entry in bold here,
        // because having different text formatting in one cell is not possible otherwise

        const paddingLeft = 1.75;
        const paddingTop = 4.8;

        doc.setFontSize(10);
        // @ts-expect-error it doesn't let me not specify the font family
        doc.setFont(undefined, 'bold');

        const row = computedTransactions[data.row.index];

        doc.text(
          `${row.typeOfEntry}`,
          data.cell.x + paddingLeft,
          data.cell.y + paddingTop
        );
      }
    },
    didDrawPage: function (data) {
      if (data.pageNumber === 1) {
        // Set Title Style
        doc.setFontSize(12);
        doc.setTextColor(TITLE_GREY);
        // @ts-expect-error it doesn't let me not specify the font family
        doc.setFont(undefined, 'bold');

        const titlePos = data.settings.startY - 5;

        // Add Title
        doc.text(
          t('resident-pdf.document.pending-transactions-table'),
          data.settings.margin.left + 1.5,
          titlePos
        );
      }
    },
    startY: pendingTableStartY,
    margin: {
      top: BASELINE_TOP_MARGIN,
      bottom: BASELINE_BOTTOM_MARGIN,
      left: BASELINE_LEFT_MARGIN,
      right: BASELINE_RIGHT_MARGIN,
    },
  });
};

function getImageLabel(
  image: ReceiptAttachment,
  context: Context,
  isPending = false
): string {
  const { t, fCurrencyCents, fDate } = context;
  const { sequenceNumber, transactionData, count, receiptImagesLength } = image;
  const {
    receiptType,
    serviceProvider,
    amount,
    additionalFeeAmount,
    receiptDate,
  } = transactionData;

  const formattedAmount = fCurrencyCents(
    Math.abs(amount + additionalFeeAmount)
  );
  const formattedDate = receiptDate
    ? ` ${t('common.dated')} ${fDate(receiptDate, YEAR_MONTH_DAY_SHORT_FORMAT)}`
    : '';

  const imagesInfo =
    receiptImagesLength > 1
      ? ` (${count} ${t('common.of')} ${receiptImagesLength})`
      : '';
  const formattedReceiptType = receiptType
    ? ` ${t(`receiptType.${receiptType}`)} -`
    : '';
  const formattedServiceProvider = serviceProvider
    ? ` ${
        serviceProvider.length > 23
          ? `${serviceProvider.substring(0, 20)}...`
          : serviceProvider
      } -`
    : '';

  const sequenceId = transactionData.sequenceID;
  const prefix = isPending
    ? `${t('pdf-export.pending-receipt-type')} #${sequenceNumber}`
    : sequenceId ?? `#${sequenceNumber}`;

  return `${prefix}:${formattedReceiptType}${formattedServiceProvider} ${formattedAmount}${formattedDate}${imagesInfo}`;
}

function getCancellationLabel(cancellationDate: Date, context: Context) {
  const { t, fDate } = context;

  const formattedDate = fDate(cancellationDate, YEAR_MONTH_DAY_SHORT_FORMAT);

  return t('resident-pdf.cancellation-note', { date: formattedDate });
}

async function resizeAndCompressImage(
  attachment: ReceiptAttachment
): Promise<File> {
  const imgData = await attachment.image.data;

  return await imageCompression(
    new File(
      [imgData],
      'file.' +
        (attachment.image.fileType
          ? attachment.image.fileType.replace(/image\//, '')
          : ''),
      {
        type: attachment.image.fileType ?? undefined,
      }
    ),
    {
      maxSizeMB: 2,
      maxWidthOrHeight: 1920,
      useWebWorker: true,
    }
  );
}

function getImageDimensions(
  img: HTMLImageElement,
  maxWidth: number,
  maxHeight: number
) {
  const imgWidth = img.width;
  const imgHeight = img.height;
  const aspectRatio = imgWidth / imgHeight;

  // Calculate the width and height of the image on the page
  let width = Math.min(imgWidth, maxWidth);
  let height = width / aspectRatio;
  if (height > maxHeight) {
    height = maxHeight;
    width = height * aspectRatio;
  }
  return {
    width,
    height,
  };
}

export const drawReceiptImages = async (
  context: Context,
  images: ReceiptAttachment[],
  doc: JsPDF,
  options?: { isPending?: boolean }
): Promise<void> => {
  // Define the width and height of each page
  const pageWidth = doc.internal.pageSize.getWidth() - 70;
  const pageHeight = doc.internal.pageSize.getHeight() - 70;

  const fontSize = 11;
  doc.setFontSize(fontSize);
  doc.setTextColor('black');
  // @ts-expect-error it doesn't let me not specify the font family
  doc.setFont(undefined, 'normal');

  // Loop through the images array
  for (let i = 0; i < images.length; i++) {
    // Add a new page to the PDF document
    doc.addPage();

    // Load the current image and get its width and height
    const compressedImage = await resizeAndCompressImage(images[i]);

    const img: HTMLImageElement = await new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = URL.createObjectURL(compressedImage);
    });

    const cancellation = images[i].transactionData.cancellation;
    const cancellationDate = images[i].transactionData.cancellationDate;

    // Draw label and compute dynamic image Y position
    const label = getImageLabel(images[i], context, options?.isPending);
    const labelY = BASELINE_TOP_MARGIN + 9.5;
    const labelPadding = 2;

    doc.text(label, 35, labelY, {
      maxWidth: pageWidth,
    });

    const labelDimensions = doc.getTextDimensions(label, {
      maxWidth: pageWidth,
    });
    let imageY = labelY + labelDimensions.h + labelPadding;

    if (cancellation && cancellationDate) {
      const cancellationLabel = getCancellationLabel(cancellationDate, context);
      doc.text(cancellationLabel, 35, imageY, {
        maxWidth: pageWidth,
      });
      const cancellationDimensions = doc.getTextDimensions(cancellationLabel, {
        maxWidth: pageWidth,
      });
      imageY += cancellationDimensions.h + labelPadding;
    }

    const availableHeight = pageHeight - (imageY - BASELINE_TOP_MARGIN);
    const { width, height } = getImageDimensions(
      img,
      pageWidth,
      availableHeight
    );

    // Add the current image to the page
    doc.addImage({
      imageData: img,
      x: 35,
      y: imageY,
      width,
      height,
      // Enable compression, because the image is resized but in terms of quality it is not compressed
      compression: 'MEDIUM',
      format: 'JPEG',
    });
  }
};

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const usePrintPdfForResident = () => {
  const { t } = useTranslation();
  const { fCurrencyCents } = useFormatting();
  const fDate = useFormatDate();

  const handlePrintPdfForResident = async ({
    dateFrom,
    dateTo,
    resident,
    receiptImages,
    numberOfWithdrawals,
    numberOfWithdrawalCancellations,
    numberOfDeposits,
    numberOfDepositCancellations,
    amountOfDepositsCents,
    amountOfDepositAdditionalFeesCents,
    amountOfDepositIncludedFeesCents,
    amountOfWithdrawalsCents,
    amountOfWithdrawalAdditionalFeesCents,
    amountOfWithdrawalIncludedFeesCents,
    balanceEndCents,
    logo,
    allEntries,
    pendingReceipts,
    pendingPayments,
    pendingReceiptImages,
    pdfFiles = [],
    pendingPdfFiles = [],
  }: HandlePrintProps) => {
    const doc = new JsPDF();

    const context: Context = {
      t,
      fCurrencyCents: (amount, opts) =>
        fCurrencyCents(amount, {
          ...opts,
          useNonBreakingCharacters: false,
        }),
      fDate,
    };

    const isToday =
      format(dateTo, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd');

    // Filter out failed/frozen payments once at the beginning
    const activePendingPayments =
      pendingPayments?.filter(
        (payment) =>
          payment.pending_charge_status !== PendingChargeStatus.failed &&
          payment.pending_charge_status !== PendingChargeStatus.frozen
      ) || [];

    const pendingReceiptsTotal =
      !pendingReceipts || pendingReceipts.length <= 0
        ? undefined
        : pendingReceipts?.reduce((sum, receipt) => sum + receipt.amount, 0);

    const pendingReceiptsAdditionalFeeTotal =
      !pendingReceipts || pendingReceipts.length <= 0
        ? undefined
        : pendingReceipts?.reduce(
            (sum, receipt) => sum + receipt.additional_fee_amount,
            0
          );

    const pendingReceiptsIncludedFeeTotal =
      !pendingReceipts || pendingReceipts.length <= 0
        ? undefined
        : pendingReceipts?.reduce(
            (sum, receipt) => sum + receipt.included_fee_amount,
            0
          );

    const pendingPaymentsTotal =
      activePendingPayments.length <= 0 || !isToday
        ? undefined
        : activePendingPayments.reduce(
            (sum, payment) => sum + payment.amount,
            0
          );

    drawOverview(context, doc, {
      residentBalance: balanceEndCents,
      withdrawalCount: numberOfWithdrawals,
      withdrawalCancellationCount: numberOfWithdrawalCancellations,
      withdrawalAmount: amountOfWithdrawalsCents,
      withdrawalAdditionalFeeAmount: amountOfWithdrawalAdditionalFeesCents,
      withdrawalIncludedFeeAmount: amountOfWithdrawalIncludedFeesCents,
      depositCount: numberOfDeposits,
      depositCancellationCount: numberOfDepositCancellations,
      depositAmount: amountOfDepositsCents,
      depositAdditionalFeeAmount: amountOfDepositAdditionalFeesCents,
      depositIncludedFeeAmount: amountOfDepositIncludedFeesCents,
      dateFrom,
      dateTo,
      pendingReceiptsTotal,
      pendingReceiptsAdditionalFeeTotal,
      pendingReceiptsIncludedFeeTotal,
      pendingPaymentsTotal,
      pendingReceiptsCount: pendingReceipts?.length || 0,
      pendingPaymentsCount: activePendingPayments.length,
    });
    // @ts-expect-error again function exists but poor typing

    const startY = doc.lastAutoTable?.finalY
      ? // @ts-expect-error again function exists but poor typing
        doc.lastAutoTable.finalY + 20
      : 0;

    const hasPendingReceipts = pendingReceipts && pendingReceipts.length > 0;
    const hasPendingPayments = isToday && activePendingPayments.length > 0;

    if (hasPendingReceipts || hasPendingPayments) {
      drawPendingTransactions(
        context,
        doc,
        pendingReceipts,
        isToday ? activePendingPayments : null,
        startY,
        balanceEndCents,
        resident,
        pendingPdfFiles
      );
    }

    const balanceStartCents =
      balanceEndCents -
      amountOfDepositsCents -
      amountOfDepositAdditionalFeesCents -
      amountOfWithdrawalsCents -
      amountOfWithdrawalAdditionalFeesCents;

    drawAccountStatement(
      context,
      doc,
      allEntries,
      balanceStartCents,
      receiptImages,
      pdfFiles,
      dateFrom
    );

    if (receiptImages.length > 0) {
      await drawReceiptImages(context, receiptImages, doc);
    }

    // Draw pending receipt images
    if (pendingReceiptImages.length > 0) {
      await drawReceiptImages(context, pendingReceiptImages, doc, {
        isPending: true,
      });
    }

    // display the header, intentionally left here in the bottom
    await drawHeaders(context, doc, {
      residentName: `${resident.firstName} ${resident.lastName}`,
      dateTo,
      dateFrom,
      nursingHomeName: resident.nursingHome.name,
      logo,
    });

    drawFooters(context, doc);

    const documentType = t('transactions.account-overview.pdfName');
    const dateFromString = format(dateFrom, 'yyyy-MM-dd');
    const dateToString = format(dateTo, 'yyyy-MM-dd');

    const title = `${resident.lastName} ${resident.firstName} - ${documentType} - ${dateFromString} - ${dateToString}.pdf`;

    // Uncomment to open the pdf in a new tab to debug more easily
    // window.open(doc.output('bloburl'));

    return doc.save(makeSafeFilename(title));
  };

  return {
    handlePrintPdfForResident,
  };
};
